import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { storeAuth } from '../auth';
import { useUser } from '../providers/UserProvider';
import * as authRepository from '../repositories/authRepository';
import type { GoogleExchangeResponse } from '../repositories/authRepository';
import { ensureDailyBriefSubscription } from '../repositories/readerRepository';
import { clearReaderAction, resumeReaderAction } from '../utils/readerPendingAction';

// Error codes the backend callback sends (app/routers/reader_auth.py).
const CALLBACK_ERROR_MESSAGES: Record<string, string> = {
  access_denied: 'Google sign-in was canceled.',
  password_required: 'An account already uses this email. Sign in with your password instead.',
};

interface Redeemed { data: GoogleExchangeResponse; subscribed: boolean; verificationSent: boolean; resumed: boolean }

// Replays the hide or feed save that sent the reader to sign in, as the dialog
// does after email sign-in; sessionStorage survives the round trip to Google.
async function resume(): Promise<boolean> {
  try {
    await resumeReaderAction();
    return true;
  } catch {
    return false;
  }
}

// One redemption per code for the life of the page. Storing the session
// remounts the route tree (LocalReaderPreferences keys on the signed-in
// account), and StrictMode runs effects twice; either would otherwise spend
// the single-use code a second time and fail.
const redemptions = new Map<string, Promise<Redeemed>>();

function redeem(code: string): Promise<Redeemed> {
  let redemption = redemptions.get(code);
  if (!redemption) {
    redemption = (async () => {
      const data = await authRepository.exchangeGoogleCode(code);
      // Stored before the subscription request so it is authenticated; the
      // session is published (login) once it finishes, as in the dialog.
      storeAuth(data);
      let subscribed = true;
      let verificationSent = false;
      if (data.daily_brief) {
        try {
          verificationSent = (await ensureDailyBriefSubscription()) === 'verification_sent';
        } catch {
          subscribed = false;
        }
      }
      return { data, subscribed, verificationSent, resumed: await resume() };
    })();
    redemptions.set(code, redemption);
  }
  return redemption;
}

/**
 * Where the backend's Google sign-in callback returns the browser. The URL
 * carries a single-use exchange code (or an error), never a session token;
 * every exit uses `replace` so the code does not stay in history.
 */
export default function AuthCallbackPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useUser();
  const [failed, setFailed] = useState(false);
  const [subscriptionFailed, setSubscriptionFailed] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);
  const [resumeFailed, setResumeFailed] = useState(false);
  const [busy, setBusy] = useState(true);
  const session = useRef<GoogleExchangeResponse | null>(null);
  const code = searchParams.get('code');
  const error = searchParams.get('error');

  const finish = () => {
    const data = session.current!;
    login(data);
    navigate(data.return_to, { replace: true });
  };
  const retryResume = async () => {
    setBusy(true);
    if (await resume()) finish();
    else setBusy(false);
  };
  const skipResume = () => {
    clearReaderAction();
    finish();
  };
  const retrySubscription = async () => {
    setBusy(true);
    try {
      if (await ensureDailyBriefSubscription() === 'verification_sent') {
        setSubscriptionFailed(false);
        setVerificationSent(true);
        setBusy(false);
      } else finish();
    } catch {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (!code) {
      const message = CALLBACK_ERROR_MESSAGES[error ?? ''] ?? 'Google sign-in failed. Please try again.';
      navigate('/login', { replace: true, state: { message } });
      return;
    }
    let disposed = false;
    redeem(code).then(({ data, subscribed, verificationSent: sent, resumed }) => {
      if (disposed) return;
      session.current = data;
      if (subscribed && !sent && resumed) finish();
      else { setSubscriptionFailed(!subscribed); setVerificationSent(sent); setResumeFailed(!resumed); setBusy(false); }
    }).catch(() => {
      if (!disposed) { setFailed(true); setBusy(false); }
    });
    return () => { disposed = true; };
    // finish/login/navigate are stable for this landing; the code is the input.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, error]);

  return <main className="auth-callback">
    {busy && <p role="status">Signing you in…</p>}
    {failed && <p role="alert">We couldn’t finish signing you in with Google. <Link to="/login" replace>Back to sign in</Link></p>}
    {resumeFailed && !subscriptionFailed && !busy && <>
      <p role="alert">You’re signed in, but we couldn’t finish your action.</p>
      <button type="button" onClick={() => void retryResume()}>Try again</button>
      <button type="button" onClick={skipResume}>Continue without it</button>
    </>}
    {/* Google did not vouch for this mailbox, so the brief waits for the link (PRO-701). */}
    {verificationSent && !resumeFailed && !busy && <>
      <p role="status">You’re signed in. We sent a link to {session.current?.user.email} to confirm your email address; once you confirm it, turn on your daily brief in Email Settings.</p>
      <button type="button" onClick={finish}>Continue to my feed</button>
    </>}
    {subscriptionFailed && !busy && <>
      <p role="alert">You’re signed in, but we couldn’t save your daily brief subscription.</p>
      <button type="button" onClick={() => void retrySubscription()}>Retry subscription</button>
      <button type="button" onClick={finish}>Continue to my feed</button>
    </>}
  </main>;
}
